import { Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { BehaviorSubject, combineLatest, filter, Subject } from 'rxjs';

import { IWebServerResponseApprover } from '@shared-interfaces/api';

import { ApproverHandler } from '../scripts/approvers';
import { BillingTimesheet } from '../scripts/BillingTimesheet';
import { CatsDb } from '../scripts/catsDB';
import { ContractManager } from '../scripts/contracts/contract-manager';
import { DateHandler } from '../scripts/date-handler';
import { DetailPayneHandler } from '../scripts/detailPayneHandler';
import { NonbillableCodes } from '../scripts/nonbillable';
import { PayrollTimesheet } from '../scripts/PayrollTimesheet';
import { UserHandler } from '../scripts/userHandler';
import { ApiService } from './api.service';
import { LogRocketService } from './log-rocket.service';
import { MessageService } from './message.service';
import { ModalService } from './modal.service';

export interface IApplicationQueryParams extends Params {
  wedate: string;
  approver: string;
  order?: string;
}

@Injectable({
  providedIn: 'root',
})

//injecting this service gives us access to data across components, meaning we can control when searches occur, and when observables are destroyed for a better user experience.
export class DatahubService {
  private readonly readySubject$ = new BehaviorSubject(false); //this casts when the application is ready to be rendered. Normally this means we don't have all the asynchronous info we need from the user object yet
  public readonly ready$ = this.readySubject$.asObservable();

  private readonly recalcTotalsSubject$ = new BehaviorSubject(false); //this is cast when the application needs to recalculate totals after completing an asynchronous operation. This would be difficult
  public readonly recalcTotals$ = this.recalcTotalsSubject$.asObservable();

  private readonly unfocusTableSubject$ = new Subject<boolean>();
  public readonly unfocusTable$ = this.unfocusTableSubject$.asObservable();

  private readonly switchApproverSubject$ = new Subject<IWebServerResponseApprover>();
  public readonly switchApprover$ = this.switchApproverSubject$.asObservable();
  //to move to the component level, because there are multiple tables that need to listen to this event.

  private readonly navigatorSubject$ = new Subject<string>();
  public readonly navigator$ = this.navigatorSubject$.asObservable();

  private readonly paramsSubject$ = new Subject<IApplicationQueryParams>();
  public readonly params$ = this.paramsSubject$.asObservable();

  public readonly weDate = new DateHandler(); //this class tracks the weekending date across the application.
  public readonly user = new UserHandler(this);
  public readonly nonBillable = new NonbillableCodes(this);
  public readonly contracts = new ContractManager(this);
  public readonly catsDB = new CatsDb(this); //this interface contains methods and storage for sorting and finding cats records.
  public readonly payrollTimesheet = new PayrollTimesheet(this, this.modalService); // this interface stores and controls data for rendering a timesheet.
  public readonly billingTimesheet = new BillingTimesheet(this, this.modalService);
  public readonly approvers = new ApproverHandler(this);

  public readonly detailPayneHandler = new DetailPayneHandler(this, this.modalService); //this interface controls the side window for multiple view. Also uses CONFIG and VALUES to render.

  public get loggedInUserEmployeeId(): string {
    return this.user.getEmpId();
  }

  constructor(
    public readonly apiService: ApiService, //these injected services will be available in the application to anyone who subscribes to datahub.
    public readonly messageService: MessageService,
    public readonly logRocket: LogRocketService,
    private readonly modalService: ModalService,
  ) {
    this.user.ready$.pipe(filter((isReady) => isReady)).subscribe({
      next: () => {
        this.readySubject$.next(true);
      },
      error: (e) => {
        console.error('Error waiting for user to be ready:', e);
      },
    });
    combineLatest([this.contracts.ready$, this.user.ready$]).subscribe({
      next: ([areContractsReady, isUserReady]: boolean[]) => {
        if (!areContractsReady || !isUserReady) return;
        this.readySubject$.next(true); //allow the view to load after the contracts have been found.
      },
      error: (e) => {
        console.error('Error combining ready observables:', e);
      },
    });
  }

  public navigate(url: string): void {
    this.navigatorSubject$.next(url);
  }

  public unfocusTable(): void {
    this.unfocusTableSubject$.next(true);
  }

  public recalcTotals(): void {
    this.recalcTotalsSubject$.next(true);
  }

  public switchApprover(approver: IWebServerResponseApprover): void {
    this.switchApproverSubject$.next(approver);
  }

  public updateQueryParams(params?: IApplicationQueryParams): void {
    //updates the state of the app from query parameters
    if (params == null) {
      this.paramsSubject$.next({
        wedate: this.weDate.getSapWeDateString(),
        approver: this.user.approvingId,
      });
    } else {
      this.paramsSubject$.next(params);
    }
  }
}
